import React, { useState } from 'react';
import { useDevelopmentDemo } from '../../contexts/DevelopmentDemoContext';
import { illustrativeRubricNote } from '../../data/developmentDomains';
import { DemoSectionHeader } from '../demoShell/DemoSectionHeader';
import { errorClass, fieldClass, glassPanel, labelClass, primaryButton } from '../../utils/demoUi';
import { deriveStage, openRound, stageLabels, submissionsFor } from '../../utils/developmentDemo';
import { StageBadge } from './DevBadges';
import { ResponseRecord } from './ResponseRecord';
export function DevTaskView() {
  const {
    state,
    config,
    dispatch
  } = useDevelopmentDemo();
  const participantId = state.ui.actingParticipantId;
  const stage = deriveStage(state, participantId);
  const round = openRound(state, participantId);
  const submissions = submissionsFor(state, participantId);
  const latest = submissions[submissions.length - 1];
  const task = config.tasks[round ?? latest?.round ?? 'initial'];
  const draft = state.ui.responseDrafts[participantId] ?? '';
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft.trim()) {
      setError('Write a response before submitting.');
      return;
    }
    dispatch({
      type: 'submitResponse',
      participantId
    });
    setError('');
    setMessage('Response submitted. It is now awaiting review.');
  };
  return <div data-id="element-722" data-mp="3msbkn2zymfs8">
      <DemoSectionHeader title="My Task" description={`${config.organization} · ${stageLabels[stage].participant}`} data-id="element-723" data-mp="3mstmqg9azo82" />

      <article aria-labelledby="task-title" className={`${glassPanel} p-6 lg:p-8`} data-id="element-724" data-mp="3qtdjvjtvmru7">
        <div className="flex flex-wrap items-center gap-3" data-id="element-725" data-mp="3vrf3q6z4mv6q">
          <p className="text-xs font-semibold text-ink-muted" data-id="element-726" data-mp="4d9e26dvw9u3">
            {task.round === 'initial' ? 'Initial task' : 'Reassessment · follow-up scenario'} · Task {task.id} · version 1
          </p>
          <StageBadge stage={stage} data-id="element-727" data-mp="38249778ddx11" />
        </div>
        <h2 id="task-title" className="mt-2 font-heading text-2xl font-bold leading-snug text-ink-dark" data-id="element-728" data-mp="380t6laqnuvbi">
          {task.title}
        </h2>
        <div className="mt-5 rounded-xl border-l-4 border-navy/40 bg-surface-bg px-5 py-4" data-id="element-729" data-mp="a9avbuh3jzq1">
          <p className="text-xs font-semibold text-ink-muted" data-id="element-730" data-mp="o0fy35frh84l">Scenario · fictional</p>
          <p className="mt-1 leading-relaxed text-ink-dark" data-id="element-731" data-mp="o2aa1u921pwj">{task.context}</p>
        </div>
        <p className="mt-5 text-lg font-semibold leading-relaxed text-ink-dark" data-id="element-732" data-mp="k0ysmc0c4azf">{task.prompt}</p>

        <h3 className="mt-6 text-sm font-semibold text-ink-dark" data-id="element-733" data-mp="3sgs1c4ofci5x">Review criteria</h3>
        <ul className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3" data-id="element-734" data-mp="3nhfe3itn592t">
          {config.criteria.map(criterion => <li key={criterion.id} className="rounded-lg border border-line bg-white px-4 py-3" data-id="element-735" data-mp="57lkaetvjm1y">
              <p className="font-semibold text-ink-dark" data-id="element-736" data-mp="d7vwwdmqf6s">{criterion.label}</p>
              <p className="mt-0.5 text-sm text-ink-muted" data-id="element-737" data-mp="1gna6v537uj28">{criterion.description}</p>
            </li>)}
        </ul>
        <p className="mt-3 text-xs text-ink-muted" data-id="element-738" data-mp="1bsebpw2onfkw">{illustrativeRubricNote}</p>

        {round ? <form onSubmit={submit} noValidate className="mt-8 border-t border-line pt-6" data-id="element-739" data-mp="1k9ey6xkton6k">
            <label htmlFor="response" className={labelClass} data-id="element-740" data-mp="1o6b2z5botafk">
              {stage === 'revision' ? 'Your revised response' : 'Your response'}
            </label>
            <p className="text-xs text-ink-muted" data-id="element-741" data-mp="1f8r1pwdtre4y">Drafts are kept in this browser until you submit.</p>
            <textarea id="response" rows={8} value={draft} onChange={event => {
          setMessage('');
          dispatch({
            type: 'setResponseDraft',
            participantId,
            text: event.target.value
          });
        }} aria-invalid={Boolean(error)} aria-describedby={error ? 'response-error' : undefined} className={fieldClass} data-id="element-742" data-mp="22yv6i72r1jxj" />
            {error && <p id="response-error" className={errorClass} data-id="element-743" data-mp="2qneawbiohckc">
                {error}
              </p>}
            <button type="submit" className={`mt-4 ${primaryButton}`} data-id="element-744" data-mp="122d1zh7sjfvu">
              Submit response
            </button>
          </form> : <p role="status" className="mt-8 rounded-lg border border-line bg-surface-bg px-4 py-3 text-ink-dark" data-id="element-745" data-mp="t0lvia5ff50x">
            {message || stageLabels[stage].participant}
          </p>}
      </article>

      {submissions.length > 0 && <section aria-labelledby="previous-submissions" className="mt-10" data-id="element-746" data-mp="1lo9e5jn4zlkx">
          <h2 id="previous-submissions" className="font-heading text-lg font-bold text-ink-dark" data-id="element-747" data-mp="1gpobldkewptb">
            My submissions and feedback
          </h2>
          <p className="mt-1 text-sm text-ink-muted" data-id="element-748" data-mp="1bxmyxqjc0x2r">Earlier responses and reviews are kept when you revise or reassess.</p>
          <ul className="mt-4 space-y-4" data-id="element-749" data-mp="24fsefips0lj9">
            {[...submissions].reverse().map(submission => <li key={submission.id} data-id="element-750" data-mp="1zpc0lxi1mxwd">
                <ResponseRecord submission={submission} data-id="element-751" data-mp="1zoabwq83ucw6" />
              </li>)}
          </ul>
        </section>}
    </div>;
}